import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '@/shared/components/BottomSheet';
import { Button } from '@/shared/components/Button';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { LeaderboardUserSummary } from './LeaderboardUserSummary';
import { LeaderboardTabNav } from './LeaderboardTabNav';
import { LeaderboardList } from './LeaderboardList';
import { LeaderboardSkeleton } from './LeaderboardSkeleton';
import { useUserStats, useLeaderboardEntries } from '../hooks/useLeaderboardData';
import type { LeaderboardScope, LeaderboardPeriod } from 'shared-types';
import { getCountryCode } from '@/lib/country-codes';

interface LeaderboardBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LeaderboardBottomSheet({ isOpen, onClose }: LeaderboardBottomSheetProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeScope, setActiveScope] = useState<LeaderboardScope>('world');
  const [activePeriod, setActivePeriod] = useState<LeaderboardPeriod>('week');

  // Convert full country name (e.g. "Madagascar") to ISO code (e.g. "mg") for API
  const country = activeScope === 'country' ? getCountryCode(user?.country) : undefined;

  const {
    data: userStats,
    isLoading: isLoadingStats,
    isError: isStatsError,
    refetch: refetchStats
  } = useUserStats({
    scope: activeScope,
    period: activePeriod,
    country,
    enabled: isOpen
  });

  const {
    data: leaderboardData,
    isLoading: isLoadingEntries,
    isError: isEntriesError,
    refetch: refetchEntries
  } = useLeaderboardEntries({
    scope: activeScope,
    period: activePeriod,
    country,
    enabled: isOpen
  });

  const isLoading = isLoadingStats || isLoadingEntries;
  const isError = isStatsError || isEntriesError;

  const handleRetry = () => {
    void refetchStats();
    void refetchEntries();
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('leaderboard.title')} className="!p-2">
      {isLoading ? (
        <LeaderboardSkeleton />
      ) : isError ? (
        <div className="flex flex-col items-center justify-center gap-4 py-10">
          <svg
            className="w-12 h-12 text-red-400 opacity-60"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
            <path
              d="M12 8v4M12 16h.01"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <Button variant="blue" onClick={handleRetry} className="h-10 px-6">
            {t('matchmaking.tryAgain')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <LeaderboardUserSummary
            rank={userStats?.rank ?? 0}
            username={userStats?.pseudo ?? user?.pseudo ?? '—'}
            score={userStats?.points ?? 0}
            gamesPlayed={userStats?.matchesPlayed ?? 0}
            wins={userStats?.wins ?? 0}
            losses={userStats?.losses ?? 0}
          />
          <LeaderboardTabNav
            activeScope={activeScope}
            activePeriod={activePeriod}
            onScopeChange={setActiveScope}
            onPeriodChange={setActivePeriod}
          />
          <LeaderboardList
            entries={leaderboardData?.leaderboard ?? []}
            currentUserPseudo={userStats?.pseudo}
          />
        </div>
      )}
    </BottomSheet>
  );
}

export default LeaderboardBottomSheet;
